import React, { type FC, useCallback, useEffect, useState } from "react";
import { Card, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { thunks as strategyThunks } from "src/thunks/strategy-wizard";
import { useDispatch, useSelector } from "src/store";
import { Container } from "@mui/system";
import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { createRoute } from "react-router-url-params";
import { paths } from "src/paths";
import { useParams } from "react-router";
import toast from "react-hot-toast";
import { tokens } from "src/locales/tokens";
import { SECTION_KEYS } from "src/consts";
import { TextForm } from "../wizard/section-forms/text-form";
import { thunks } from "src/thunks/strategy-wizard";

export const BrandDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const user = useAuth();

  const { strategyId } = useParams();
  const steps = useSelector((state) => state.strategyWizard.steps);
  //const wizardStep = useSelector((state) => state.strategyWizard.wizardStep);
  const isFinished = useSelector((state) => state.strategyWizard.isFinished);
  const currentSection = steps[0];
  //const currentValue = currentSection.strategyStep.value;
  //console.log(currentSection);
  const [isSaving, setIsSaving] = useState(false);

  const router = useRouter();

  const navigateToNextStep = useCallback(async () => {
    router.push(
      createRoute(paths.strategy.targetAudience).link({
        strategyId: strategyId,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const saveChanges = useCallback(
    async (value: any) => {
      await dispatch(
        strategyThunks.saveStrategySection(
          strategyId!,
          SECTION_KEYS.BRAND_DETAILS,
          value
        )
      );
      await navigateToNextStep();
      await dispatch(strategyThunks.navigateToNextStep());
    },
    [dispatch, navigateToNextStep, strategyId]
  );

  const currentValue = currentSection.strategyStep.value;

  const generateIdeas = useCallback(async () => {
    await dispatch(
      strategyThunks.generateOptions(
        strategyId!,
        SECTION_KEYS.BRAND_DETAILS,
        i18n.language,
        false
      )
    );
  }, [dispatch, i18n.language, strategyId]);

  const getSectionValue = useCallback(async () => {
    try {
      const result = await dispatch(
        thunks.getStrategySectionValue(
          strategyId!,
          currentSection.strategyStep.configuration.sectionKey!,
          i18n.language
        )
      );
      if (!result.text) {
        generateIdeas();
      }
    } catch (error) {
      if (error.code === "Profile.SectionValueNotFound") {
        console.log("Section value not found");
        generateIdeas();
      } else {
        throw error;
      }
    }
  }, [
    currentSection.strategyStep.configuration.sectionKey,
    dispatch,
    i18n.language,
    strategyId,
    generateIdeas,
  ]);

  useEffect(() => {
    if (currentSection.strategyStep.configuration.sectionKey) {
      getSectionValue();
    } else {
      generateIdeas();
    }
  }, [
    dispatch,
    getSectionValue,
    currentSection.strategyStep.configuration.sectionKey,
    generateIdeas,
  ]);

  useEffect(() => {
    if (currentSection && currentSection.hasSavingError) {
      toast.error(t(tokens.general.formError));
    }
  }, [currentSection, t]);

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: {
          xs: 2,
          sm: 4,
        },
        mb: 4,
      }}
    >
      <Card
        sx={{
          p: 4,
          mb: 2,
        }}
      >
        <Stack
          direction={"column"}
          spacing={3}
        >
          <TextForm
            key={SECTION_KEYS.BRAND_DETAILS}
            value={
              currentValue?.text ||
              (currentSection?.strategyStep?.instructionResult
                ?.options as any as string)
            }
            // placeholder={t(tokens.wizard.placeholders.valueProposition) ?? undefined}
            isMultiline={true}
            isSubmitting={
              isSaving ||
              currentSection?.strategyStep?.instructionResult?.isOptionsLoading
            }
            onSubmit={async (text) => {
              await saveChanges({ text: text });
            }}
          />
        </Stack>
      </Card>
    </Container>
  );
};
